import type { SiteUserDTO } from "@lumora/bridge";
import { ConfirmGate } from "./ConfirmGate.js";
import { formatCreated, SITE_ACCOUNT_ROLES } from "./siteAccounts.js";

export type SiteAccountsListProps = {
  users: SiteUserDTO[];
  onReset: (user: SiteUserDTO) => void;
  onRoleChange: (user: SiteUserDTO, role: string) => void;
};

/** Account rows for a site: email, role, created, reset password, role select. */
export function SiteAccountsList({ users, onReset, onRoleChange }: SiteAccountsListProps) {
  return (
    <ul className="platform-account-list" aria-label="Site accounts">
      {users.map((u) => (
        <li key={u.id} className="platform-account-row" data-account-email={u.email}>
          <div className="platform-account-main">
            <strong className="platform-account-email">{u.email}</strong>
            {u.primary ? (
              <span className="platform-account-badge" data-account-primary>
                Owner (billing / Stripe)
              </span>
            ) : (
              <label className="platform-account-role">
                <span className="muted">Role</span>
                <select
                  value={u.role}
                  onChange={(e) => onRoleChange(u, e.target.value)}
                  aria-label={`Role for ${u.email}`}
                >
                  {!(SITE_ACCOUNT_ROLES as readonly string[]).includes(u.role) ? (
                    <option value={u.role}>{u.role}</option>
                  ) : null}
                  {SITE_ACCOUNT_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <span className="muted platform-account-created">
              Created {formatCreated(u.createdAt)}
            </span>
          </div>
          <ConfirmGate
            idleLabel="Reset password"
            confirmLabel="Yes, email reset link"
            onConfirm={() => onReset(u)}
          />
        </li>
      ))}
    </ul>
  );
}

export type StatusMessageProps = { msg: string | null; err: string | null };

/** Status / error text. Wraps long URLs; announced to assistive tech. */
export function StatusMessage({ msg, err }: StatusMessageProps) {
  return (
    <>
      {msg ? (
        <p className="muted platform-provision-status" role="status" aria-live="polite">
          {msg}
        </p>
      ) : null}
      {err ? (
        <p className="ops-error platform-provision-status" role="alert">
          {err}
        </p>
      ) : null}
    </>
  );
}
